/**
 * Capacity Analysis — re-sourcé sur la nouvelle pile (cascade Cost assignment +
 * CHR yearly + Effective time), comme Profitability analysis.
 *
 * Layout (cards) :
 *  - 4 KPI cards : utilization, CCR avg, cost of non-billable, cost of idle
 *  - Card 1 : Usage breakdown (stacked bar, h ↔ CHF)
 *  - Card 2 : Metrics per pool
 *  - Card 3 : Metrics per person
 *
 * Méthodo : full cost = productive labour (LCR×TR) + supervision + expenses
 *  + specific indirect + indirect personnel (absenteism + corporate − indemnités)
 *  + indirect charges (résiduel).
 *   CCR = full cost ÷ TR (practical capacity).  burden = indirects ÷ TR.
 *   CCR individuel = LCR_personne + burden(pool).
 *   Idle cost @CCR = Σ idle_i × CCR_i (par personne, pas au taux moyen).
 *   BCR = full cost ÷ billable hours.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { usePersistentValue, usePersistentValueLocal } from '../../hooks/usePersistentState';
import { useTableSort } from '../../hooks/useTableSort';
import { normalizeName, buildSearchKey } from '../../utils/personTable';
import CardShell from '../../components/CardShell';
import SegmentedToggle from '../../components/SegmentedToggle';
import PillFilter from '../../components/PillFilter';
import SearchField from '../../components/SearchField';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import PersonTh from '../../components/PersonTh';
import { fetchPersonnelCostCascade, readCascadeMode } from '../../api/tdabc/personnelCostCascade';
import { getChrYearly } from '../../api/chrYearly';
import { getTimeEffective } from '../../api/hrTimeEffective';
import { listCostPools } from '../../api/tdabc/cost_pools';
import PageHeader from '../../components/PageHeader';
import './CapacityAnalysisPage.css';


const UNASSIGNED = '__UNASSIGNED__';

// ── Formatters ────────────────────────────────────────────────────────────────

const FMT_INT = new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 });
const FMT_2   = new Intl.NumberFormat('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtH(v)   { return FMT_INT.format(Number(v) || 0); }
function fmtCHF(v) { return FMT_INT.format(Number(v) || 0); }
function fmtRate(v){ return FMT_2.format(Number(v) || 0);   }
function fmtPct(v) {
  return `${(Number(v) * 100).toLocaleString('fr-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}
function errorMessage(err, fallback) {
  return err?.response?.data?.detail || err?.message || fallback;
}

function utilClass(v) {
  const n = Number(v);
  if (n >= 0.85) return 'green';
  if (n >= 0.70) return 'amber';
  return 'red';
}

const DASH = <span style={{ color: '#D1D5DB' }}>—</span>;

// ── Chart colours / segments ────────────────────────────────────────────────────


// ── Re-sourcing : cascade + CHR + time → rows (pool) + persons + kpi ──────────────

export function buildCapacity(cascade, chr, tenantId, overheadIds, timeEff) {
  if (!cascade || !chr) return { rows: [], persons: [], kpi: {} };
  const oh = overheadIds || new Set();
  // Supervision (team mgmt) hours par personne — depuis Annual hours.
  const supByPerson = {};
  for (const r of ((timeEff && timeEff.rows) || [])) supByPerson[r.person_id] = Number(r.team_mgmt_hours) || 0;
  const mode = readCascadeMode(tenantId);
  const ct = cascade.totals || {};
  const totalTr = ct.production_hours || 0;
  const totalTf = ct.billed_hours || 0;
  const totSpec = ct.specific_indirect || 0;
  const ciResid = Math.max(0, (ct.indirect_costs || 0) - totSpec);
  const alloc = (comp, m, pool, residualTotal) => {
    if (m === 'within') return pool[comp] || 0;
    const tot = residualTotal != null ? residualTotal : (ct[comp] || 0);
    if (m === 'billed') return totalTf > 0 ? (tot / totalTf) * (pool.billed_hours || 0) : 0;
    return totalTr > 0 ? (tot / totalTr) * (pool.production_hours || 0) : 0;
  };

  // Agrégats CHR par pool : OT, supervision h, billable, non-billable, TR, revenu std.
  // idleLcr / nbLcr = Σ heures × LCR par personne (pour valoriser idle / non-billable au CCR individuel).
  const chrBy = {};
  for (const r of (chr.rows || [])) {
    const key = r.cost_pool_id || UNASSIGNED;
    const b = chrBy[key] || (chrBy[key] = { ot: 0, supH: 0, tf: 0, nf: 0, tr: 0, revenue: 0, billed: 0, idleLcr: 0, nbLcr: 0, fte: 0, supLcr: 0 });
    const tf = Number(r.heures_tf) || 0;
    const nf = Number(r.heures_nf) || 0;
    const tr = Number(r.heures_prod) || 0;
    const lcr = Number(r.lcr) || 0;
    const idle = Math.max(0, tr - tf - nf);
    b.ot   += Number(r.heures_contractuelles) || 0;
    b.supH += supByPerson[r.person_id] || 0;
    b.tf   += tf;
    b.nf   += nf;
    b.tr   += tr;
    b.revenue += (Number(r.standard_price) || 0) * tf;     // revenu simulé (prix standard × billable) — source Input Analytics
    b.billed  += r.billed_amount != null ? Number(r.billed_amount) : 0;  // revenu réel facturé (page Revenues)
    b.idleLcr += idle * lcr;          // Σ idle_i × LCR_i
    b.nbLcr   += nf * lcr;            // Σ non-billable_i × LCR_i
    b.fte     += Number(r.fte) || 0;  // Σ FTE (activité × mois/12)
    b.supLcr  += (supByPerson[r.person_id] || 0) * lcr;  // Σ supervision_i × LCR_i
  }

  const burdenByKey = {};  // taux indirect du pool (CHF/h de TR) — pour le CCR individuel
  const nameByKey   = {};

  const rows = (cascade.pools || []).filter(p => !oh.has(p.pool_id)).map(p => {
    const key = p.is_unassigned ? UNASSIGNED : p.pool_id;
    const a = chrBy[key] || { ot: 0, supH: 0, tf: 0, nf: 0, tr: 0, revenue: 0, billed: 0, idleLcr: 0, nbLcr: 0, fte: 0, supLcr: 0 };
    const idleH = Math.max(0, a.tr - a.tf - a.nf);
    const sup     = alloc('supervision', mode.supervision, p);
    const exp     = alloc('expenses', mode.expenses, p);
    const corp    = alloc('corporate', mode.corporate, p);
    const indirectMode = mode.indirect_costs === 'billed' ? 'billed' : 'shared';
    const indirectResid = alloc('indirect_costs', indirectMode, p, ciResid);
    const specific  = p.specific_indirect || 0;
    const productive = p.productive_labour || 0;

    // full cost = productive labour + indirect alloué au pool.
    // Refonte CCR 2026-07 : congés spéciaux (absenteism) HORS périmètre (indemnisés,
    // déchargés de la capacité ET de la charge) et indemnités perçues IGNORÉES
    // (produits hors périmètre) → base = productif + supervision + corp + frais + indirect.
    // (Aligne buildCapacity sur la cascade People/CCR : Σ TR×CCR = coût total.)
    const fullCost = productive + sup + exp + specific + corp + indirectResid;
    const ccr = a.tr > 0 ? fullCost / a.tr : 0;          // CCR = full cost ÷ TR
    const burden = a.tr > 0 ? (fullCost - productive) / a.tr : 0;  // indirects ÷ TR
    const bcr = a.tf > 0 ? fullCost / a.tf : 0;          // BCR = full cost ÷ billable
    burdenByKey[key] = burden;
    nameByKey[key]   = p.pool_name;

    // Idle cost @CCR = Σ idle_i × CCR_i = Σ idle_i×LCR_i + burden × Σidle_i.
    const costOfIdle        = a.idleLcr + burden * idleH;        // = under-absorption
    const costOfNonBillable = a.nbLcr + burden * a.nf;           // Σ non-billable_i × CCR_i
    // Absorption pure : Σ (billable + non-billable + idle) × CCR_i = full cost.
    const costBillableCcr   = fullCost - costOfNonBillable - costOfIdle;  // Σ billable_i × CCR_i
    // ── P&L @ CCR ── revenu réel facturé (page Revenues), pas de tarif théorique.
    const effRev    = a.billed;
    const netMargin = effRev - costBillableCcr - costOfNonBillable;       // = effRev − full cost + idle
    const operating = effRev - fullCost;                                  // = netMargin − under-absorption

    return {
      key, pool_id: p.pool_id, pool_name: p.pool_name, fte: a.fte,
      production_h: a.tr, working_h: a.ot, pool_mgmt_h: a.supH,
      billable_h: a.tf, non_billable_h: a.nf, idle_h: idleH,
      utilization_rate: a.tr > 0 ? (a.tf + a.nf) / a.tr : 0,
      full_cost: fullCost, ccr, bcr,
      cost_of_idle: costOfIdle,
      cost_of_non_billable: costOfNonBillable,
      // Coûts par segment au CCR individuel (pour le graphique CHF, cohérent avec le tableau)
      cost_pool_mgmt: a.supLcr + burden * a.supH,   // supervision × CCR_i
      cost_billable: costBillableCcr,
      cost_non_billable: costOfNonBillable,
      cost_idle: costOfIdle,
      // ── P&L (au CCR) ──
      effective_revenue: effRev,
      cost_billable_ccr: costBillableCcr,
      cost_nonbillable_ccr: costOfNonBillable,
      prod_cost: costBillableCcr + costOfNonBillable,
      under_absorption: costOfIdle,
      net_margin: netMargin,
      operating_profit: operating,
    };
  });

  // ── Lignes par personne (mêmes métriques, au CCR individuel) ──
  const persons = [];
  for (const r of (chr.rows || [])) {
    if (r.cost_pool_id && oh.has(r.cost_pool_id)) continue;   // pools overhead exclus
    const key = r.cost_pool_id || UNASSIGNED;
    const to = Number(r.heures_contractuelles) || 0;   // TO = temps d'ouverture (heures contractuelles)
    const tr = Number(r.heures_prod) || 0;
    const tf = Number(r.heures_tf) || 0;
    const nf = Number(r.heures_nf) || 0;
    const lcr = Number(r.lcr) || 0;
    const idle = Math.max(0, tr - tf - nf);
    const burden = burdenByKey[key] || 0;
    const ccr = lcr + burden;
    const fullCost = ccr * tr;
    const costOfIdle = idle * ccr;
    const prodCost   = (tf + nf) * ccr;                          // (billable + non-billable) × CCR
    const billed     = r.billed_amount != null ? Number(r.billed_amount) : 0;
    persons.push({
      key: r.person_id,
      person_name: `${r.last_name || ''} ${r.first_name || ''}`.trim() || r.person_name || '—',
      last: (r.last_name || '').trim(),
      first: (r.first_name || '').trim(),
      pool_name: nameByKey[key] || r.cost_pool_name || 'Unassigned',
      fte: Number(r.fte) || 0,
      // Champs portés depuis CHR yearly (vue Profitability par personne) :
      status: r.status || null,                                          // active/joined/left/left_before
      activity_rate: Number(r.taux_occupation) || 0,                     // taux d'activité contractuel (%)
      lcr,                                                               // LCR = salaire chargé ÷ TR (CHF/h)
      standard_price: r.standard_price != null ? Number(r.standard_price) : null,  // prix standard horaire (hypothèse)
      loaded_payroll: Number(r.loaded_payroll) || 0,                     // salaire chargé annuel (gross + CS empr)
      expenses_amount: Number(r.expenses_amount) || 0,                   // frais directs individuels annuels
      working_h: to, production_h: tr, billable_h: tf, non_billable_h: nf, idle_h: idle,
      utilization_rate: tr > 0 ? (tf + nf) / tr : 0,
      full_cost: fullCost, ccr, bcr: tf > 0 ? fullCost / tf : 0,
      cost_of_idle: costOfIdle,
      // ── P&L (au CCR individuel) ──
      effective_revenue: billed,
      prod_cost: prodCost,
      net_margin: billed - prodCost,
      operating_profit: billed - fullCost,
    });
  }
  persons.sort((x, y) => Number(y.ccr) - Number(x.ccr));

  // Heures dues pour un FTE à 100 % = Σ OT ÷ Σ FTE (heures contractuelles plein temps).
  const totOt  = rows.reduce((s, r) => s + (Number(r.working_h) || 0), 0);
  const totFte = rows.reduce((s, r) => s + (Number(r.fte) || 0), 0);
  const hoursPerFte = totFte > 0 ? totOt / totFte : 0;
  // Practical FTE = TR ÷ heures pour 100 % FTE → équivalent FTE après retrait du non-productif.
  for (const r of rows)    r.practical_fte = hoursPerFte > 0 ? r.production_h / hoursPerFte : 0;
  for (const p of persons) p.practical_fte = hoursPerFte > 0 ? p.production_h / hoursPerFte : 0;

  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const tTr = sum('production_h'), tTf = sum('billable_h'), tNf = sum('non_billable_h'), tFull = sum('full_cost');
  const kpi = {
    utilization_avg: tTr > 0 ? (tTf + tNf) / tTr : 0,
    ccr_avg: tTr > 0 ? tFull / tTr : 0,
    full_cost_total: tFull,                 // coût complet annuel (pour re-baser les coûts sur heures audit)
    tr_total: tTr,                          // TR de la cascade coût (base du CCR)
    cost_of_non_billable_total: sum('cost_of_non_billable'),
    cost_of_idle_total: sum('cost_of_idle'),
  };
  return { rows, persons, kpi };
}

// ── Sous-composants ──────────────────────────────────────────────────────────

export function UtilBar({ rate }) {
  const uc = utilClass(rate);
  return (
    <div className="ca2-util-wrap">
      <div className="ca2-util-bar-track">
        <div className={`ca2-util-bar-fill ca2-util-bar--${uc}`}
             style={{ width: `${Math.min(100, Number(rate) * 100).toFixed(1)}%` }} />
      </div>
      <span className={`ca2-util-label ca2-util-label--${uc}`}>{fmtPct(rate)}</span>
    </div>
  );
}

// Tableau de métriques générique (par pool ou par personne).
// `lead` = { headers: [<th>…], cells: (row) => [<td>…], footer: <td colSpan=…> }
export { fmtH, fmtCHF, fmtRate, fmtPct, utilClass, pnlSign, DASH };

export function CapacityTable({ rows, lead, footLabel, sort, onSort, eptPct = false, moneyUnit = 'chf' }) {
  // Montants en CHF (défaut) ou kCHF (÷1000) selon la bascule du hub.
  const { t } = useTranslation();
  const p = useP();
  const fmtMoney = (v) => (moneyUnit === 'kchf' ? fmtCHF(Math.round((Number(v) || 0) / 1000)) : fmtCHF(v));
  const Th = ({ col, label, title, align = 'right', style }) => {
    const cls = align === 'center' ? 'th-center' : 'th-right';
    if (!onSort) return <th className={cls} title={title} style={style}>{label}</th>;
    const active = sort && sort.key === col;
    return (
      <th className={`${cls} ca2-th-sort${active ? ' ca2-th-sort--active' : ''}`} title={title} style={style}
          onClick={() => onSort(col, 'desc')}>
        {label}{active && <span className="ca2-sort-ind"> {sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </th>
    );
  };
  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const tEpt = sum('ept_capa');
  const tTr = sum('cap_tr'), tProd = sum('prod_h'), tNonprod = sum('nonprod_h'), tIdleCost = sum('idle_cost');
  const tCcrTr = rows.reduce((s, r) => s + (Number(r.ccr) || 0) * (Number(r.cap_tr) || 0), 0);
  const tCcr        = tTr > 0 ? tCcrTr / tTr : 0;                  // CCR moyen pondéré par le TR
  const tPcr        = tProd > 0 ? (tCcr * tTr) / tProd : 0;        // PCR global = CCR×TR ÷ TF
  const tNonprodRate = Math.max(0, tPcr - tCcr);
  const tNonprodPct  = tTr > 0 ? tNonprod / tTr : 0;
  return (
    <div className="ca2-table-wrap">
      <table className="ca2-table">
        <thead>
          <tr>
            {lead.headers}
            <Th col="ept_capa" label={p('EPT capa')} title={p("Capacité pratique en EPT = TR ÷ TO d'1 EPT @100 %")} />
            <Th col="cap_tr" label={t('phrases:p_91644115a8')} title={t('phrases:p_1ae916ee30')} />
            {/* Mêmes noms que la saisie (2026-09-01) : TF et NF, pas
                « productif » et « non-productif ». */}
            <Th col="prod_h" label={p('Heures TF')} title={p('Temps de production (TF)')} />
            <Th col="nonprod_h" label={p('Heures NF')} title={t('phrases:p_9220fc9f9b')} />
            <Th col="nonprod_pct" label={p('% NF')} />
            <Th col="ccr" label="CCR" title={t('phrases:p_a8b1f1f8cd')} />
            <Th col="nonprod_rate" label={t('phrases:p_1c48702043')} title={p('PCR − CCR = (TR − TF) × CCR ÷ TF')} />
            <Th col="pcr" label="PCR" title={p('Coût de production = coût capacité (CCR×TR) ÷ TF')} />
            <Th col="idle_cost" label={t('phrases:p_a0fba8f8b1')} title={t('phrases:p_a50fe322dd')} />
            <Th col="cost_total" label={t('phrases:p_004b64cd2b')} title={t('phrases:p_645626fcdb')} />
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
              /* `_cls` permet à l'appelant de peindre une ligne de GROUPE
                 (standard de groupement) sans que la table sache pourquoi. */
              <tr key={row.key} className={row._cls || undefined}>
                {lead.cells(row)}
                <td className="ca2-td-num">{eptPct ? fmtPct(row.ept_capa) : fmtRate(row.ept_capa)}</td>
                <td className="ca2-td-num ca2-td-h">{fmtH(row.cap_tr)}</td>
                <td className="ca2-td-num ca2-td-h">{fmtH(row.prod_h)}</td>
                <td className="ca2-td-num ca2-td-h ca2-td-muted">{fmtH(row.nonprod_h)}</td>
                <td className="ca2-td-num">{fmtPct(row.nonprod_pct)}</td>
                <td className="ca2-td-num ca2-td-ccr">{fmtRate(row.ccr)}</td>
                <td className="ca2-td-num">{fmtRate(row.nonprod_rate)}</td>
                <td className="ca2-td-num ca2-td-ccr">{fmtRate(row.pcr)}</td>
                <td className="ca2-td-num ca2-td-terra">{fmtMoney(row.idle_cost)}</td>
                <td className="ca2-td-num"><strong>{fmtMoney(row.cap_tr * row.ccr)}</strong></td>
              </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="ca2-tr-footer">
            <td className="ca2-td-pool ca2-td-footer-label" colSpan={lead.headers.length}>{footLabel}</td>
            <td className="ca2-td-num"><strong>{fmtRate(tEpt)}</strong></td>
            <td className="ca2-td-num ca2-td-h">{fmtH(tTr)}</td>
            <td className="ca2-td-num ca2-td-h">{fmtH(tProd)}</td>
            <td className="ca2-td-num ca2-td-h ca2-td-muted">{fmtH(tNonprod)}</td>
            <td className="ca2-td-num">{fmtPct(tNonprodPct)}</td>
            <td className="ca2-td-num ca2-td-ccr">{fmtRate(tCcr)}</td>
            <td className="ca2-td-num">{fmtRate(tNonprodRate)}</td>
            <td className="ca2-td-num ca2-td-ccr">{fmtRate(tPcr)}</td>
            <td className="ca2-td-num ca2-td-terra"><strong>{fmtMoney(tIdleCost)}</strong></td>
            <td className="ca2-td-num"><strong>{fmtMoney(tCcrTr)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Couleur sémantique des montants de marge (Net margin / Profit) dans les tables.
function pnlSign(v) { return v > 0 ? 'ca2-pnl-pos' : v < 0 ? 'ca2-pnl-neg' : ''; }

// ── Main component ────────────────────────────────────────────────────────────

export default function CapacityAnalysisPage({ activeTenant, addToast, year: yearProp, embedded = false, tablesOnly = false, overview = null, afterSynth = null, hideMetricsTables = false, moneyUnit = 'chf' }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const year = yearProp ?? new Date().getFullYear();   // année globale (topbar)

  const [cascade, setCascade]   = useState(null);
  const [chr, setChr]           = useState(null);
  const [timeEff, setTimeEff]   = useState(null);
  const [costPools, setCostPools] = useState([]);
  const [loading, setLoading]   = useState(false);
  const personSortApi = useTableSort(`tdabc_cap_sort_${tenantId}`, 'ccr', 'desc');
  const { sortCol: personSortCol, sortDir: personSortDir, handleSort: handlePersonSort,
          sortRows: sortPersonRows, nameOrder } = personSortApi;
  const [search, setSearch] = useState('');
  // « Actif » = statut backend (contrat ouvert à ce jour), porté par les lignes CHR.
  const [status, setStatus] = usePersistentValue(`tdabc_cap_status_v2_${tenantId}`, 'all');
  /* UNE table, deux lectures (2026-08-27) : par groupe ou par personne. En vue
     Groupe, un clic sur le groupe déplie les personnes qui le composent. */
  /* Par PERSONNE au repos (2026-09-01), comme les deux autres tables de la
     chaîne : c'est la lecture qu'on vient chercher, le groupe en est
     l'agrégat. Le choix est RETENU d'une visite à l'autre : qui travaille par
     groupe ne doit pas rebasculer à chaque venue. */
  const [costView, setCostView] = usePersistentValueLocal(
    `ca2_cost_view_${tenantId || ''}`, 'person');
  const [openPools, setOpenPools] = useState(() => new Set());
  const [groupSel, setGroupSel] = useState(() => new Set());
  const toggleCostPool = (name) => setOpenPools(prev => {
    const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n;
  });

  useEffect(() => {
    if (!tenantId) { setCascade(null); setChr(null); setTimeEff(null); setCostPools([]); return; }
    setLoading(true);
    Promise.all([
      fetchPersonnelCostCascade(tenantId, year),
      getChrYearly(tenantId, year).then(r => { if (r?.error) throw new Error(r.error); return r.data || r; }),
      getTimeEffective(tenantId, year).then(r => (r && r.data) ? r.data : r),
      listCostPools(tenantId),
    ])
      .then(([cas, c, te, cp]) => {
        setCascade(cas); setChr(c); setTimeEff(te);
        setCostPools((cp && cp.data) || cp || []);
      })
      .catch(e => addToast(errorMessage(e, 'Failed to load capacity analysis'), 'error'))
      .finally(() => setLoading(false));
  }, [tenantId, year]); // eslint-disable-line

  const { rows, persons, kpi } = useMemo(() => {
    const overheadIds = new Set((costPools || []).filter(p => p.is_overhead_pool).map(p => p.id));
    return buildCapacity(cascade, chr, tenantId, overheadIds, timeEff);
  }, [cascade, chr, timeEff, tenantId, costPools]); // eslint-disable-line


  // Table Coût Capacité : heures ANNUELLES (Temps de production saisi = billable_hours
  // annuel, source du CCR) valorisées au CCR INDIVIDUEL. TR = capacité pratique
  // (dénominateur du CCR) ; productif = TF annuel ; non-productif = solde TR − TF.
  // Périmètre = pools productifs ; personne sans CCR = exclue (pas de repli).
  // Heures d'1 EPT à 100 % = base TO (temps d'ouverture) → dénominateur de l'« EPT
  // capa » (TR ÷ TO@100 %). Décision CFO 2026-07 : base TO, pas TT.
  const hoursPerFte = (() => {
    const totOt = rows.reduce((s, r) => s + (Number(r.working_h) || 0), 0);
    const totFte = rows.reduce((s, r) => s + (Number(r.fte) || 0), 0);
    return totFte > 0 ? totOt / totFte : 0;
  })();
  const capPersons = persons.map(p => {
    const ccr = Number(p.ccr) || 0;
    const tr = Number(p.production_h) || 0;   // TR = capacité pratique (dénominateur CCR)
    const tf = Number(p.billable_h) || 0;     // heures productives = TF annuel saisi
    const nf = Number(p.non_billable_h) || 0;
    const idle = Number(p.idle_h) || 0;
    const nonprod = Math.max(0, tr - tf);
    const pcr = tf > 0 ? (ccr * tr) / tf : 0;   // PCR = coût capacité (CCR×TR) ÷ TF
    return {
      ...p,
      first_name_sort: normalizeName(p.first),
      last_name_sort: normalizeName(p.last || p.person_name),
      _search: buildSearchKey(p.first, p.last || p.person_name, p.pool_name),
      ept_capa: hoursPerFte > 0 ? tr / hoursPerFte : 0,   // EPT capa = TR ÷ TO@100 %
      ccr,
      cap_tr: tr,
      prod_h: tf,                                  // heures productives = TF
      nonprod_h: nonprod,                          // heures non-productives = TR − TF
      nonprod_pct: tr > 0 ? nonprod / tr : 0,
      pcr,                                         // coût de production (ex-BCR)
      nonprod_rate: Math.max(0, pcr - ccr),        // coût horaire du temps non productif
      util_rate: tr > 0 ? tf / tr : 0,
      idle_h: idle,
      idle_cost: idle * ccr,
      nb_cost: nf * ccr,
      _tf: tf,
    };
  });
  const ccrAvg = (() => {
    const c = rows.reduce((s, r) => s + (Number(r.full_cost) || 0), 0);
    const t = rows.reduce((s, r) => s + (Number(r.production_h) || 0), 0);
    return t > 0 ? c / t : 0;
  })();
  /* Heures NF au sens PLEIN (décision Olivier 2026-09-01) : NF client +
     projets + absences + capacité non utilisée + sur-production — tout ce que
     la capacité n'a pas facturé. Le coût du non-facturable les valorise au
     CCR moyen. */
  const nfHours = (o) => o
    ? (Number(o.nonbill) || 0) + (Number(o.project) || 0) + (Number(o.absence) || 0) + Math.abs(Number(o.idle) || 0)
    : 0;

  // Pools = agrégat des personnes (⇒ total table = total bandeau, mêmes CCR individuels).
  // Fonction, et non calcul figé : les sous-totaux doivent porter sur les
  // personnes RETENUES par les filtres (2026-08-27).
  const aggregatePools = (list) => {
  const _byPool = {};
  for (const cp of list) {
    const k = cp.pool_name || 'Unassigned';
    const b = _byPool[k] || (_byPool[k] = { key: k, pool_name: k, n: 0, ept_capa: 0, cap_tr: 0, prod_h: 0, _tf: 0, idle_h: 0, idle_cost: 0, _ccrtr: 0 });
    b.n += 1;
    b.ept_capa += cp.ept_capa; b.cap_tr += cp.cap_tr; b.prod_h += cp.prod_h; b._tf += cp._tf;
    b.idle_h += cp.idle_h; b.idle_cost += cp.idle_cost; b._ccrtr += cp.ccr * cp.cap_tr;
  }
  return Object.values(_byPool).map(b => {
    const ccr = b.cap_tr > 0 ? b._ccrtr / b.cap_tr : 0;
    const nonprod = Math.max(0, b.cap_tr - b._tf);
    const pcr = b._tf > 0 ? (ccr * b.cap_tr) / b._tf : 0;
    return {
      ...b, ccr,
      nonprod_h: nonprod,
      nonprod_pct: b.cap_tr > 0 ? nonprod / b.cap_tr : 0,
      pcr,
      nonprod_rate: Math.max(0, pcr - ccr),
      util_rate: b.cap_tr > 0 ? b._tf / b.cap_tr : 0,
      idle_pct: b.cap_tr > 0 ? b.idle_h / b.cap_tr : 0,
    };
  }).sort((a, b) => (a.pool_name || '').localeCompare(b.pool_name || '', 'fr'));
  };
  const capPools = aggregatePools(capPersons);

  // ── Guards ──────────────────────────────────────────────────────────────────
  if (!activeTenant) {
    return (
      <main className="page-table-layout is-fluid is-compact">
        <div className="page-table-empty">Select a company.</div>
      </main>
    );
  }
  if (loading) {
    return (
      <main className="page-table-layout is-fluid is-compact">
        <p className="page-table-loading">Loading…</p>
      </main>
    );
  }

  // ── Chart data ──────────────────────────────────────────────────────────────

  const poolLead = {
    headers: [<th key="pool" className="th-left">Groupe</th>],
    cells: (row) => [<td key="pool" className="ca2-td-pool">{row.pool_name}</td>],
  };
  // ── Tri table « Metrics per person » (hook partagé) ──
  const personSort = { key: personSortCol, dir: personSortDir };
  const pInd = (col) => (personSortCol === col ? (personSortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const personLead = {
    headers: [
      <PersonTh key="person" sort={personSortApi} className="th-left" />,
      <th key="pool" className={`th-left ca2-th-sort${personSortCol === 'pool' ? ' ca2-th-sort--active' : ''}`}
          onClick={() => handlePersonSort('pool')} title={p('Cliquer pour trier')}>{p('Groupe')}{pInd('pool')}</th>,
    ],
    cells: (row) => [
      <td key="person" className="ca2-td-pool">
        {(row.first || row.last)
          ? <PersonName firstName={row.first} lastName={row.last} order={nameOrder} />
          : row.person_name}
      </td>,
      <td key="pool" className="ca2-td-pool ca2-td-muted">{row.pool_name}</td>,
    ],
  };

  /* Filtres de la table « Coût par personne » : statut + recherche, rien de
     plus. Les pastilles par groupe ressources sont parties le 2026-08-26 — la
     card d'AVANT dit déjà le coût par groupe, et la recherche libre trouve un
     groupe par son nom. */
  const q = normalizeName(search.trim());
  const filteredPersons = capPersons.filter(p => {
    if (status === 'active' && !(p.status === 'active' || p.status === 'joined')) return false;
    if (groupSel.size > 0 && !groupSel.has(p.pool_name || 'Unassigned')) return false;
    if (q && !p._search.includes(q)) return false;
    return true;
  });
  const sortedPersons = sortPersonRows(filteredPersons,
    (r, col) => (col === 'pool' ? normalizeName(r.pool_name) : r[col]));


  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <main className={`page-table-layout ca2-layout is-fluid is-compact${embedded ? ' ca2-layout--embedded' : ''}`}>

      {!embedded && <PageHeader title="Capacity Analysis" />}

      {rows.length === 0 ? (
        <div className="page-table-empty">No productive pool with data for {year}.</div>
      ) : (
        <>
          {!tablesOnly && <>
          {/* ── KPI cards ── */}
          <div className="ca2-synth">
            {/* « Usage de la capacité » (2026-08-30) : la card du dessus dit ce
                dont on DISPOSE, celle-ci ce qu'on en FAIT. L'exercice et le
                nombre de groupes sortent du titre — le premier vit dans la
                barre supérieure, le second dans la table. */}
            <div className="ca2-synth__head">{p('Usage de la capacité')}</div>
            <div className="ca2-synth__split">
            <div className="ca2-synth__row ca2-synth__tiles">
              {/* EPT, Temps total (TO) et Capacité disponible (TR) sont
                  retirés le 2026-08-30 : ils venaient du moteur d'audit, qui
                  cumule les mois FACTURÉS sur le calendrier réel, quand les
                  tuiles de cascade juste au-dessus lisent l'année
                  contractuelle. Deux réponses à la même question. Ce qui reste
                  ici est l'USAGE de cette capacité, que la cascade ne dit
                  pas. */}
              {/* Quatre tuiles (2026-09-01) : Heures TF, Heures NF au sens
                  plein (NF client + projets + absences + solde), le taux TF
                  avec son N-1, et le coût du non-facturable (NF × CCR moyen).
                  « Inutilisé » et son coût sont retirés — ils redisaient le
                  solde de la cascade. */}
              {overview?.columns?.real && (() => {
                const o = overview.columns.real;
                const oPrev = overview.columns?.prev;
                const nf = nfHours(o);
                const rate = o.tr > 0 ? o.billable / o.tr : 0;
                const ratePrev = oPrev && oPrev.tr > 0 ? oPrev.billable / oPrev.tr : null;
                return (<>
                <div className="ca2-synth__item">
                  <span className="ca2-synth__label">{p('Heures TF')}</span>
                  <span className="ca2-synth__value">{fmtH(o.billable)} <small>h</small></span>
                  <span className="ca2-synth__sub">{Math.round(o.pct_tr?.billable)} {p('% du TR')}</span>
                </div>
                <div className="ca2-synth__item">
                  <span className="ca2-synth__label">{p('Heures NF')}</span>
                  <span className="ca2-synth__value">{fmtH(nf)} <small>h</small></span>
                  <span className="ca2-synth__sub">{p('NF client + projets + absences + solde')}</span>
                </div>
                <div className="ca2-synth__item">
                  <span className="ca2-synth__label">{p('Utilisation globale')}</span>
                  <span className={`ca2-synth__value ca2-synth__value--${utilClass(rate)}`}>{fmtPct(rate)}</span>
                  <span className="ca2-synth__sub">{p('taux TF (÷ TR)')}{ratePrev != null && <> · {p('N-1')} {fmtPct(ratePrev)}</>}</span>
                </div>
                <div className="ca2-synth__item">
                  <span className="ca2-synth__label">{p('Coût du non-facturable')}</span>
                  <span className="ca2-synth__value ca2-synth__value--amber">{fmtCHF(nf * ccrAvg)} <small>CHF</small></span>
                  <span className="ca2-synth__sub">{p('heures NF × CCR moyen')}</span>
                </div>
                </>);
              })()}
            </div>
            {/* « Où va le temps payé » et la « ligne de flottaison » sont
                retirées le 2026-08-30 : elles lisaient un TO cumulé sur les
                seuls mois facturés, quand les tuiles du dessus lisent l'année
                contractuelle. Deux TO à l'écran, aucun moyen de savoir lequel
                répond. */}
            </div>
          </div>

          {afterSynth}
          </>}


          {!hideMetricsTables && (() => {
            /* UNE seule card (2026-08-27) : la bascule choisit la lecture, et
               en vue Groupe le clic déplie les personnes du groupe. Les
               sous-totaux portent sur les personnes retenues par les filtres. */
            const poolsView = aggregatePools(filteredPersons);
            const poolNames = [...new Set(capPersons.map(cp => cp.pool_name || 'Unassigned'))]
              .sort((a, b) => a.localeCompare(b, 'fr'));
            const activeFilters = groupSel.size + (status === 'active' ? 1 : 0) + (search.trim() ? 1 : 0);
            // Lignes affichées : groupes (dépliables) ou personnes, jamais les deux.
            const rows = costView === 'person' ? sortedPersons : poolsView.flatMap(g => {
              const open = openPools.has(g.pool_name);
              const head = { ...g, _kind: 'group', _open: open, _cls: 'ca2-grp-row' };
              if (!open) return [head];
              const kids = sortedPersons
                .filter(pp => (pp.pool_name || 'Unassigned') === g.pool_name)
                .map(pp => ({ ...pp, _kind: 'child' }));
              return [head, ...kids];
            });
            const lead = {
              headers: costView === 'person' ? personLead.headers : [
                <th key="pool" className="th-left">{p('Groupe')}</th>,
                <th key="n" className="th-left">{p('Pers.')}</th>,
              ],
              cells: (row) => (costView === 'person' ? personLead.cells(row) : (
                row._kind === 'group' ? [
                  <td key="pool" className="ca2-td-pool cbt-td-click" role="button" tabIndex={0}
                      aria-expanded={row._open} title={p('Voir les personnes')}
                      onClick={() => toggleCostPool(row.pool_name)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCostPool(row.pool_name); }
                      }}>
                    <span className={`cbt-chev${row._open ? ' is-open' : ''}`} aria-hidden>{row._open ? '▾' : '▸'}</span>
                    {row.pool_name}
                  </td>,
                  <td key="n" className="ca2-td-pool ca2-td-muted">{row.n}</td>,
                ] : [
                  <td key="person" className="ca2-td-pool cbt-td-sub">
                    {(row.first || row.last)
                      ? <PersonName firstName={row.first} lastName={row.last} order={nameOrder} />
                      : row.person_name}
                  </td>,
                  <td key="n" className="ca2-td-pool ca2-td-muted" />,
                ]
              )),
            };
            return (
              <CardShell
                flush
                title={<>{p('Coût de production')} <small>· {year}</small></>}
                viewControls={(
                  <SegmentedToggle
                    label={p('Vue')} value={costView} onChange={setCostView}
                    options={[
                      { value: 'person', label: p('Personne') },
                      { value: 'pool', label: p('Groupe') },
                    ]}
                  />
                )}
                filters={(
                  <>
                    <FilterIcon />
                    <PillFilter
                      label={p('Groupe')} value={groupSel} onChange={setGroupSel}
                      options={poolNames.map(n => ({ value: n, label: n }))}
                    />
                    <SegmentedToggle
                      compact
                      label={p('Statut')} value={status} onChange={setStatus}
                      options={[
                        { value: 'all', label: p('Tous') },
                        { value: 'active', label: p('Actifs'), tone: 'alert' },
                      ]}
                    />
                    <FilterCount count={activeFilters}
                                 onClear={() => { setGroupSel(new Set()); setStatus('all'); setSearch(''); }} />
                  </>
                )}
                search={<SearchField value={search} onChange={setSearch}
                                     placeholder={p('Personne ou groupe…')} />}
                filterCount={activeFilters}
                collapsible storageKey="cout-prod-cout"
              >
                <CapacityTable
                  rows={rows} lead={lead}
                  sort={costView === 'person' ? personSort : undefined}
                  onSort={costView === 'person' ? handlePersonSort : undefined}
                  eptPct={costView === 'person'} moneyUnit={moneyUnit}
                  footLabel={costView === 'person'
                    ? <>TOTAL / MOYENNE <span className="ca2-footer-count">({sortedPersons.length} {p('personnes')})</span></>
                    : <>{p('TOTAL')} <span className="ca2-footer-count">({poolsView.length} {p('groupe(s)')})</span></>}
                />
              </CardShell>
            );
          })()}
        </>
      )}
    </main>
  );
}
